import * as React from 'react';
import { useEffect, useRef } from 'react';

/** Material UI Packages. */
import { Typography, Box, TextField, Autocomplete } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Autosuggest Highlight Library. */
import parse from 'autosuggest-highlight/parse';

/** Internal Components. */
import { updateSocketData } from 'shared/lib/fetchSocket';
import {
    geocodedLocationString,
    isSamePlaceOption,
    placeOptionLabel,
    placeOptionParts,
    useGooglePlacesAutocomplete,
} from 'shared/lib/handleGoogle';

export default function GoogleRenderCell({ params, shouldFocus, reducer, tableName, hasRowUpdate, tableId }) {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking). */
    const tabValue = useSelector((state) => state.tabValueForm);

    const value = params.value;
    const api = params.api;
    const { options, handleInputChange, endSession } = useGooglePlacesAutocomplete({ value });

    const inputRef = useRef(null);
    useEffect(() => {
        if (shouldFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [shouldFocus]);

    /** Which suggestion Enter would pick, and whether we owe the grid a commit once it resolves. */
    const highlightedRef = useRef(null);
    const commitAfterGeocodeRef = useRef(false);

    const applyPickedPlace = (newValue) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder
            .geocode({ placeId: newValue.place_id })
            .then(({ results }) => {
                if (!results?.[0]) return undefined;
                const afterCity = geocodedLocationString(results[0]);

                return Promise.resolve(
                    api.setEditCellValue({ id: params.id, field: params.field, value: afterCity })
                ).then(() => {
                    // Main-table grids (hasRowUpdate) persist on edit commit via processRowUpdate,
                    // which owns the case-closure lock and route validation — emitting here would
                    // bypass both and double-send. Sub-table grids keep the direct emit.
                    if (!hasRowUpdate) {
                        updateSocketData(dispatch, tabValue, 'cell', tableName, reducer, {
                            id: params.id,
                            key: params.field,
                            value: afterCity,
                            table_id: tableId ? tableId : [],
                        });
                    }

                    if (commitAfterGeocodeRef.current) {
                        commitAfterGeocodeRef.current = false;
                        api.stopCellEditMode({ id: params.id, field: params.field });
                    }
                });
            })
            .catch((error) => {
                commitAfterGeocodeRef.current = false;
                console.error('Error during geocoding: ', error.message);
            });
    };

    return (
        <Autocomplete
            size="small"
            sx={{
                height: '100%',
                width: '100%',
                '& .MuiInputBase-input': {
                    fontSize: '11px',
                },
                '& .MuiInputBase-root': {
                    p: 0,
                    height: '100%',
                },
                '& .MuiFormControl-root': {
                    height: '100%',
                },
            }}
            getOptionLabel={placeOptionLabel}
            isOptionEqualToValue={isSamePlaceOption}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            disableClearable
            value={value}
            noOptionsText="No locations"
            onHighlightChange={(event, option) => {
                highlightedRef.current = option;
            }}
            onKeyDown={(event) => {
                // Enter picks the highlighted suggestion, and the grid ends the edit on that same
                // keystroke — before the geocode that produces the value. Hold the edit open here
                // and commit once the value lands, so Enter still saves the picked place.
                if (hasRowUpdate && event.key === 'Enter' && highlightedRef.current) {
                    event.stopPropagation();
                    commitAfterGeocodeRef.current = true;
                }
            }}
            onChange={(event, newValue) => {
                endSession();
                // The committed value rides along as an option; re-picking it has nothing to look up.
                if (!newValue?.place_id) {
                    commitAfterGeocodeRef.current = false;
                    return;
                }
                applyPickedPlace(newValue);
            }}
            onInputChange={handleInputChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputRef={inputRef}
                    variant="standard"
                    sx={{
                        px: 1,
                        '& .MuiInputBase-root': {
                            border: 'none',
                        },
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                const { mainText, matches, secondaryText } = placeOptionParts(option);
                const parts = parse(
                    mainText,
                    matches.map((match) => [match.offset, match.offset + match.length])
                );
                return (
                    <li key={key} {...optionProps}>
                        <Grid container sx={{ alignItems: 'center' }}>
                            <Grid>
                                {parts.map((part, index) => (
                                    <Box
                                        key={index}
                                        component="span"
                                        sx={{ fontWeight: part.highlight ? 'bold' : 'regular', fontSize: '11px' }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                                {secondaryText ? (
                                    <Typography sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        {secondaryText}
                                    </Typography>
                                ) : null}
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
}
